import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ReactComponent as UploadIcon } from "../../../assets/images/icons/Upload.svg";
import { setColor } from "../../../assets/styles/styles";
import pinkCheckmark from "../../../assets/images/icons/pink-checkmark.png";
import { ReactComponent as Cross } from "../../../assets/images/icons/Cross.svg";
import LoadingIcon from "../loading/LoadingIcon";

const UploadFile = ({
    label = "Upload File",
    accept = ["image/png", "image/jpeg", "image/jpg"],
    selectedFile,
    setSelectedFile,
    uploadFn,
    status,
    setStatus,
    fileId,
    source = "uploadFile",
}) => {
    const [errorMsg, setErrorMsg] = useState("Failed to upload");
    const [isError, setIsError] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!accept.includes(file.type)) {
            if (source === "uploadFile") {
                setStatus("error");
            }
            setIsError(true);
            setErrorMsg("Invalid file type. Please select a valid file. Only .png, .jpeg, .jpg files are allowed.");
            return;
        }

        setSelectedFile(file);
        setIsError(false);
        setErrorMsg("");
        if (source === "uploadFile") {
            setStatus("");
        }
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        if (source === "uploadFile") {
            setStatus("");
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        if (status === "error") {
            const timer = setTimeout(() => {
                if (source === "uploadFile") {
                    setStatus("");
                }
                setSelectedFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [status]);

    return (
        <>
            {source === "uploadFile" && (
                <>
                    <UploadWrapper>
                        <Label htmlFor="fileUpload">{label}</Label>
                        {status === "uploading" && (
                            <>
                                <LoadingIconContainer>
                                    <LoadingIcon />
                                    <StatusBox status={status}>Uploading...</StatusBox>
                                </LoadingIconContainer>
                            </>
                        )}
                        {fileId && (
                            <>
                                <InputRow>
                                    {status === "completed" && (
                                        <>
                                            <InputBox type="text" disabled value={fileId} />
                                            <PinkCheckmark src={pinkCheckmark} alt="Intraverse Logo" />
                                        </>
                                    )}
                                </InputRow>
                            </>
                        )}
                        {!fileId && (
                            <>
                                <InputRow>
                                    <InputFile
                                        type="file"
                                        id="fileUpload"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        disabled={status === "uploading"}
                                    />

                                    {selectedFile && (
                                        <>
                                            <UploadButton
                                                onClick={uploadFn}
                                                disabled={status === "uploading" || !selectedFile}
                                            >
                                                <UploadIconStyled />
                                            </UploadButton>
                                        </>
                                    )}
                                </InputRow>
                                {selectedFile && status !== "completed" && (
                                    <StatusBox status={status}>
                                        Oops! Looks like you have a file selected. Please finish uploading it or&nbsp;
                                        <ClearLink
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleClearFile();
                                            }}
                                        >
                                            clear it
                                        </ClearLink>
                                        &nbsp;before continuing
                                    </StatusBox>
                                )}
                            </>
                        )}

                        {status === "error" && (
                            <>
                                <StatusBox status={status}>
                                    <StyledCloseIcon />
                                    {errorMsg}
                                </StatusBox>
                            </>
                        )}
                    </UploadWrapper>
                </>
            )}

            {source === "withoutCallingFileUploadAPI" && (
                <>
                    <UploadWrapper>
                        <Label htmlFor="fileUpload">{label}</Label>

                        <InputRow>
                            <InputFile
                                type="file"
                                id="fileUpload"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                disabled={status === "uploading"}
                            />
                        </InputRow>
                        {isError && (
                            <>
                                <StatusBox status={"error"}>
                                    <StyledCloseIcon />
                                    {errorMsg}
                                </StatusBox>
                            </>
                        )}
                    </UploadWrapper>
                </>
            )}
        </>
    );
};

export default UploadFile;

const UploadWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 20px;
`;

const Label = styled.label`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
    margin-left: 5px;
`;

const InputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const InputFile = styled.input`
    padding: 5px;
    flex: 1;
    margin-left: 5px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    background-color: transparent;

    &::file-selector-button {
        background-color: #444;
        color: #fff;
        border: none;
        padding: 7px 12px;
        cursor: pointer;
        border-radius: 4px;
        margin-right: 5px;

        &:disabled {
            cursor: not-allowed;
        }
    }
`;
const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;
const UploadButton = styled.button`
    background-color: transparent;
    border: none;
    border-radius: 5px;
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        background-color: #555;
        cursor: not-allowed;
    }
`;

const UploadIconStyled = styled(UploadIcon)`
    width: 35px;
    height: 35px;
`;

const StatusBox = styled.div`
    margin-left: 10px;
    color: ${({ status }) => {
        if (status === "error") return "#FF6B6B";
        if (status === "completed") return "#4CAF50";
        return "#B9AFFF";
    }};
    font-size: 14px;
`;
const ImageLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    color: #ffffff;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        background-color: #6a6a6a;
        color: #ff69b4;
        text-decoration: none;
        transform: translateY(-2px);
        box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.3);
    }

    &:active {
        transform: scale(0.98);
    }
`;
const PinkCheckmark = styled.img`
    width: 25px;
    height: 25px;
    @media (max-width: 768px) {
        width: 80px;
        margin-top: 50px;
    }
`;
const StyledCloseIcon = styled(Cross)`
    width: 10px;
    height: 10px;
    margin-right: 5px;
    #Union {
        fill: ${setColor.errorColor};
    }
`;

const LoadingIconContainer = styled.div`
    display: flex;
    padding: 15px;
    flex: 1;
    margin-left: 5px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    background-color: transparent;
`;

const ClearLink = styled.a`
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #0056b3;
    }
`;
